import React, { createContext, useContext, useRef, useCallback } from "react"
import { gql } from "@apollo/client"
import firebase from "firebase/compat/app"
import { apolloCubeClient } from "../../../../services/generic-services/helpers/studyPlan/studyPlanApolloStatic"
import { EventAction, EventCategory, EventLabel, EventContentType } from "./types"

export interface EventChainItemInput {
  type: EventAction
  category?: EventCategory
  label?: EventLabel
  contentType?: EventContentType
  contentRefType?: string
  contentRefId?: string
  attempt?: number
  score?: string
  timeSpent?: number
  itemId?: string
  program?: string
  subject?: string
  url?: string
  classId?: string
  meta?: Record<string, unknown>
  duration?: number
}

interface EventChainState {
  tailId: string | null
  buffer: EventChainItemInput[]
  isFlushing: boolean
  debounceTimer: ReturnType<typeof setTimeout> | null
  maxWaitTimer: ReturnType<typeof setTimeout> | null
  generation: number
}

interface EventChainContextValue {
  trackEvent: (event: EventChainItemInput) => void
  resetChain: () => void
}

const LOG_EVENT_CHAIN = gql`
  mutation LogEventChain($data: LogEventChainInput!) {
    eventChain {
      logEventChain(data: $data) {
        lastEventId
        count
      }
    }
  }
`

export const TAIL_ID_KEY = "eventChain_tailId"

// Flush fires at whichever comes first: FLUSH_QUIET_MS of quiet,
// FLUSH_MAX_WAIT_MS after the oldest buffered event, or the buffer
// reaching FLUSH_MAX_BATCH. Each post sends at most FLUSH_MAX_BATCH events.
const FLUSH_QUIET_MS = 500
const FLUSH_MAX_WAIT_MS = 5000
const FLUSH_MAX_BATCH = 50

const EventChainContext = createContext<EventChainContextValue | null>(null)

// Module-level singleton so Redux thunks and non-React code can call these
export const eventChain = {
  trackEvent: (_event: EventChainItemInput): void => undefined,
  resetChain: (): void => undefined,
  getTailId: (): string | null => null,
}

export const EventChainProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const stateRef = useRef<EventChainState>({
    tailId: sessionStorage.getItem(TAIL_ID_KEY),
    buffer: [],
    isFlushing: false,
    debounceTimer: null,
    maxWaitTimer: null,
    generation: 0,
  })

  // Use a ref to store scheduleFlush so flush can call it without a circular dep
  const scheduleFlushRef = useRef<() => void>(() => undefined)

  const flush = useCallback(async () => {
    const state = stateRef.current
    if (state.isFlushing || state.buffer.length === 0) return

    // Wait for Firebase auth to restore before sending — on refresh,
    // currentUser is null until auth state is restored
    if (!firebase.auth().currentUser) {
      scheduleFlushRef.current()
      return
    }

    // This flush consumes the buffer, so the max-wait clock restarts with the
    // next buffered event; the finally block drains any remainder
    if (state.maxWaitTimer !== null) {
      clearTimeout(state.maxWaitTimer)
      state.maxWaitTimer = null
    }

    const generation = state.generation
    state.isFlushing = true
    const batch = state.buffer.splice(0, FLUSH_MAX_BATCH)

    try {
      const result = await apolloCubeClient.mutate({
        mutation: LOG_EVENT_CHAIN,
        variables: {
          data: {
            events: batch,
          },
        },
      })
      // If resetChain() was called while we were in-flight, discard the result
      if (generation !== state.generation) return
      state.tailId = result?.data?.eventChain?.logEventChain?.lastEventId ?? state.tailId
      if (state.tailId) {
        sessionStorage.setItem(TAIL_ID_KEY, state.tailId)
      }
    } catch (error) {
      // Drop batch on failure; tailId state is preserved only for the
      // qti-pwa splice-anchor handoff — the server derives the chain tail
      // itself and does not use this value
      console.warn("EventChain flush failed", { error, batchLength: batch.length })
    } finally {
      state.isFlushing = false
      if (state.buffer.length > 0) {
        scheduleFlushRef.current()
      }
    }
  }, [])

  const scheduleFlush = useCallback(() => {
    const state = stateRef.current
    if (state.debounceTimer !== null) {
      clearTimeout(state.debounceTimer)
    }
    state.debounceTimer = setTimeout(() => {
      state.debounceTimer = null
      if (!state.isFlushing) {
        flush()
      }
      // If isFlushing, the finally block will schedule another flush
    }, FLUSH_QUIET_MS)
  }, [flush])

  scheduleFlushRef.current = scheduleFlush

  const trackEvent = useCallback(
    (event: EventChainItemInput) => {
      const state = stateRef.current
      state.buffer.push(event)
      // Full batch: flush now instead of waiting on timers. If a flush is
      // in-flight, its finally block drains the refilled buffer
      if (state.buffer.length >= FLUSH_MAX_BATCH && !state.isFlushing) {
        if (state.debounceTimer !== null) {
          clearTimeout(state.debounceTimer)
          state.debounceTimer = null
        }
        if (state.maxWaitTimer !== null) {
          clearTimeout(state.maxWaitTimer)
          state.maxWaitTimer = null
        }
        flush()
        return
      }
      scheduleFlush()
      // Cap how long the trailing debounce can postpone a flush: the max-wait
      // timer runs from the oldest buffered event and is not reset per event
      if (state.maxWaitTimer === null) {
        state.maxWaitTimer = setTimeout(() => {
          state.maxWaitTimer = null
          if (state.debounceTimer !== null) {
            clearTimeout(state.debounceTimer)
            state.debounceTimer = null
          }
          if (!state.isFlushing) {
            flush()
          }
          // If isFlushing, the finally block will schedule another flush
        }, FLUSH_MAX_WAIT_MS)
      }
    },
    [scheduleFlush, flush]
  )

  const resetChain = useCallback(() => {
    const state = stateRef.current
    if (state.debounceTimer !== null) {
      clearTimeout(state.debounceTimer)
      state.debounceTimer = null
    }
    if (state.maxWaitTimer !== null) {
      clearTimeout(state.maxWaitTimer)
      state.maxWaitTimer = null
    }
    state.generation += 1
    state.tailId = null
    sessionStorage.removeItem(TAIL_ID_KEY)
    state.buffer = []
    state.isFlushing = false
  }, [])

  // Flush on tab close / navigation so recent events aren't lost
  React.useEffect(() => {
    const flushOnHide = () => {
      if (document.visibilityState === "hidden") void flush()
    }
    const flushOnPageHide = () => void flush()
    document.addEventListener("visibilitychange", flushOnHide)
    window.addEventListener("pagehide", flushOnPageHide)
    return () => {
      document.removeEventListener("visibilitychange", flushOnHide)
      window.removeEventListener("pagehide", flushOnPageHide)
    }
  }, [flush])

  // Expose via module-level singleton for use outside React (e.g., Redux thunks)
  React.useLayoutEffect(() => {
    eventChain.trackEvent = trackEvent
    eventChain.resetChain = resetChain
    eventChain.getTailId = () => stateRef.current.tailId
  }, [trackEvent, resetChain])

  const contextValue: EventChainContextValue = { trackEvent, resetChain }

  return (
    <EventChainContext.Provider value={contextValue}>
      {children}
    </EventChainContext.Provider>
  )
}

export const useEventChain = (): EventChainContextValue => {
  const ctx = useContext(EventChainContext)
  if (!ctx) {
    throw new Error("useEventChain must be used within an EventChainProvider")
  }
  return ctx
}
